/**
 * WorkstationForm — Odoo-style full-screen detail view for PCs & Laptops
 *
 * Layout: fixed overlay (right of sidebar, below navbar)
 *   ├─ TopBar   : ← Back | breadcrumb | Save | Discard | Delete
 *   ├─ SmartBar : SmartButtons (type, CPU, RAM, SO, assigned, location)
 *   └─ Body (flex row, internal scroll)
 *       ├─ Main  : record title + tabs (Identificación / Hardware / Red / Ubicación)
 *       └─ Right sidebar : chatter / notes
 *
 * Tabs:
 *   1. Identificación — code, name, device_type, status, asset_tag, serial_number, os_name, os_version
 *   2. Hardware       — manufacturer, model, cpu, ram_gb, disk_type, disk_gb
 *   3. Red            — mac_address, ip_address, IPAM
 *   4. Ubicación      — assigned_to, purchase_date, room, floor, location_id, notes
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  TrashIcon,
  PencilSquareIcon,
  ComputerDesktopIcon,
  CpuChipIcon,
  WifiIcon,
  MapPinIcon,
  UserIcon,
  SparklesIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import Dialog from './common/Dialog';
import LocationTreeSelect from './common/LocationTreeSelect';
import AgentStatusBadge from './AgentStatusBadge';
import DocSmartButton from './DocSmartButton';
import CiActivityPanel from './common/CiActivityPanel';
import ElementIpPanel from './ElementIpPanel';
import DeviceAccessPanel from './DeviceAccessPanel';
import SoftwarePackagesPanel from './SoftwarePackagesPanel';
import HelpDot from '../academy/HelpDot';
import ServerDisksPanel from './ServerDisksPanel';
import AgentActivityPanel from './AgentActivityPanel';
import WorkstationAdPanel from './WorkstationAdPanel';
import WorkstationHardwareList from './WorkstationHardwareList';
import ServerLiveMonitor from './ServerLiveMonitor';
import WorkstationPerformancePanel from './WorkstationPerformancePanel';
import WorkstationPlansCard from './WorkstationPlansCard';
import WorkstationUpdatesPanel from './WorkstationUpdatesPanel';
import ServerComponentsPanel from './ServerComponentsPanel';
import SystemUsersPanel from './SystemUsersPanel';
import SoftwareInventoryPanel from './SoftwareInventoryPanel';
import RemoteActions from './RemoteActions';
import FormCardsShell from './FormCardsShell';
import FormCardsHead from './FormCardsHead';
import ContactsSmartButton from './ContactsSmartButton';
import { versionIsOlder } from '../utils/version';
import { filterSoftwareEntries } from '../utils/software';
import useAgentUpdateStatus from '../hooks/useAgentUpdateStatus';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import useNavGuard from '../hooks/useNavGuard';
import { buildLocationMap, locationPath } from '../utils/locationTree';
import { useSocket } from '../contexts/SocketContext';
import '../styles/DetailView.css';
import TabButton from './TabButton';
import DetailCrumb from './DetailCrumb';


const DEVICE_TYPES = { pc: { icon: '🖥️', label: 'PC Escritorio' }, laptop: { icon: '💻', label: 'Portátil' } };
// Cómo se instaló el agente. El parque mezcla MSI (GPO/Intune) y EXE (NSIS).
const CHANNEL_LABELS = { msi: 'MSI', exe: 'EXE', source: 'Código fuente' };

/**
 * Salud del equipo tal y como la materializa el push del agente
 * (`app/services/workstation_health.py`). La ficha y el dashboard tienen que
 * decir lo mismo, así que se pintan estos valores, no los del blob.
 */
const SALUD_BITLOCKER = {
  protected:      { label: 'Cifrado',     pill: 'ok' },
  unprotected:    { label: 'Sin cifrar',  pill: 'bad' },
  in_progress:    { label: 'En proceso',  pill: 'wa' },
  not_applicable: { label: 'No aplica',   pill: 'info' },
  unknown:        { label: 'Sin datos',   pill: 'info' },
};
const SALUD_UPDATES = {
  ok:             { label: 'Al día',      pill: 'ok' },
  pending:        { label: 'Pendientes',  pill: 'wa' },
  critical:       { label: 'Críticas',    pill: 'bad' },
  not_applicable: { label: 'No aplica',   pill: 'info' },
  unknown:        { label: 'Sin datos',   pill: 'info' },
};
const SALUD_AV = {
  protected:      { label: 'Activo y al día', pill: 'ok' },
  outdated:       { label: 'Firmas antiguas', pill: 'wa' },
  disabled:       { label: 'Deshabilitado',   pill: 'bad' },
  missing:        { label: 'Sin antivirus',   pill: 'bad' },
  not_applicable: { label: 'No aplica',       pill: 'info' },
  unknown:        { label: 'Sin datos',       pill: 'info' },
};
const SALUD_RIESGO = {
  ok:       { label: 'Sin riesgo', pill: 'ok' },
  warning:  { label: 'Aviso',      pill: 'wa' },
  critical: { label: 'En riesgo',  pill: 'bad' },
  info:     { label: 'Info',       pill: 'info' },
};
const PILL_SEVERIDAD = { critical: 'bad', warning: 'wa', info: 'info' };

// Los campos que manda el agente se pintan bloqueados: si se cambian a mano, el
// siguiente inventario los revierte y el usuario no sabe por qué.
const LOCK_STYLE = { background: 'var(--bg-toolbar)', cursor: 'not-allowed' };

const fmtFecha = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const antiguedadDesde = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const years = (Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000);
  if (years < 0) return '';
  if (years >= 1) {
    const n = Math.floor(years);
    return `${n} año${n === 1 ? '' : 's'}`;
  }
  const months = Math.max(1, Math.round(years * 12));
  return `${months} mes${months === 1 ? '' : 'es'}`;
};

const pickSalud = (ws) => ({
  bitlocker: ws?.bitlocker_state || 'unknown',
  unprotected: Number(ws?.bitlocker_unprotected_count || 0),
  updates: ws?.updates_state || 'unknown',
  pending: Number(ws?.updates_pending_count || 0),
  critical: Number(ws?.updates_critical_count || 0),
  reboot: Boolean(ws?.updates_reboot_required),
  av: ws?.av_state || 'unknown',
  avProduct: ws?.av_product || '',
  avProducts: Array.isArray(ws?.av_products) ? ws.av_products : [],
  risk: ws?.risk || null,
  inventoryAt: ws?.inventory_updated_at || null,
  osBuild: ws?.os_build || '',
  osRelease: ws?.os_release || '',
});
const STATUS_OPTS  = [
  { value: 'active',          label: 'Activo' },
  { value: 'maintenance',     label: 'Mantenimiento' },
  { value: 'inactive',        label: 'Inactivo' },
  { value: 'decommissioned',  label: 'Dado de baja' },
];

const mapWorkstationToForm = (source) => ({
  code: source?.code || '',
  name: source?.name || '',
  device_type: source?.device_type || 'pc',
  manufacturer: source?.manufacturer || '',
  model: source?.model || '',
  serial_number: source?.serial_number || '',
  cpu: source?.cpu || '',
  ram_gb: source?.ram_gb ?? '',
  disk_type: source?.disk_type || 'ssd',
  disk_gb: source?.disk_gb ?? '',
  os_name: source?.os_name || 'Windows',
  os_version: source?.os_version || '',
  mac_address: source?.mac_address || '',
  ip_address: source?.ip_address || '',
  assigned_to: source?.assigned_to || '',
  asset_tag: source?.asset_tag || '',
  purchase_date: source?.purchase_date || '',
  room: source?.room || '',
  floor: source?.floor || '',
  location_id: source?.location_id || '',
  notes: source?.notes || '',
  status: source?.status || 'active',
  domain_name: source?.domain_name ?? null,
  part_of_domain: source?.part_of_domain ?? null,
});

function resolveValidatedInventoryUser(usersPayload) {
  const validated = usersPayload?.validated_user;
  if (!validated || typeof validated !== 'object') return '';
  const raw = String(validated.raw || '').trim();
  if (raw) return raw;
  const username = String(validated.username || '').trim();
  const domain = String(validated.domain || '').trim();
  if (username && domain) return `${domain}\\${username}`;
  if (username) return username;
  return '';
}

function WorkstationForm({ workstation, companyId, initialMode = 'create', onClose, onSave, variant = 'classic', navItems = [], navIndex = -1, onNavigate, latestAgentVersion = null }) {
  const isCards = variant === 'cards';
  const isEditing = !!workstation?.id;
  const socket = useSocket();

  // Modo lectura por defecto en las fichas existentes (estándar del CMDB); el
  // alta y el botón «Editar» del listado piden `initialMode="edit"`.
  const [editing, setEditing] = useState(!isEditing || initialMode === 'edit');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // ── Form state ──
  const [form, setForm] = useState({
    code: '', name: '', device_type: 'pc', manufacturer: '', model: '',
    serial_number: '', cpu: '', ram_gb: '', disk_type: 'ssd', disk_gb: '',
    os_name: 'Windows', os_version: '', mac_address: '', ip_address: '',
    assigned_to: '', asset_tag: '', purchase_date: '', room: '', floor: '',
    location_id: '', notes: '', status: 'active',
    domain_name: null, part_of_domain: null,
  });
  const [isDirty, setIsDirty] = useState(false);

  // ── Data state ──
  const [locations, setLocations] = useState([]);
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const [modelOptions, setModelOptions]               = useState([]);
  const [addingManufacturer, setAddingManufacturer]   = useState(false);
  const [addingModel, setAddingModel]                 = useState(false);
  const [newManufacturer, setNewManufacturer]         = useState('');
  const [newModel, setNewModel]                       = useState('');

  // ── UI state ──
  const [activeTab, setActiveTab] = useState(isCards ? 'resumen' : 'identificacion');
  const [accessCount, setAccessCount] = useState(0);
  const [apiError, setApiError]   = useState(null);
  const [saving, setSaving]       = useState(false);
  const [savedOk, setSavedOk]    = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestingCode, setSuggestingCode] = useState(false);

  // Cambios sin guardar al pasar de ficha o cerrar.
  const navGuard = useNavGuard({
    dirty: isDirty,
    saving,
    entity: 'este ordenador',
    actionLabel: 'pasas a otro ordenador o cierras la ficha',
    save: () => handleSubmit(null, { keepEditing: editing }),
  });
  const [agentStatus, setAgentStatus] = useState(workstation?.agent_status || 'unknown');
  const [agentLastSeen, setAgentLastSeen] = useState(workstation?.last_seen_at || null);
  const [agentRuntime, setAgentRuntime] = useState(workstation?.agent_runtime || null);
  const [agentVersion, setAgentVersion] = useState(workstation?.agent_version || null);
  const [installChannel, setInstallChannel] = useState(workstation?.install_channel || null);
  const [installVersion, setInstallVersion] = useState(workstation?.install_version || null);
  const [salud, setSalud] = useState(() => pickSalud(workstation));
  const [showDisksPanel, setShowDisksPanel] = useState(false);
  const [showComponentsPanel, setShowComponentsPanel] = useState(false);
  const [components, setComponents] = useState([]);
  const [showUsersPanel, setShowUsersPanel] = useState(false);
  const [diskCount, setDiskCount] = useState(0);
  const [componentCount, setComponentCount] = useState(0);
  const [usersCount, setUsersCount] = useState(0);
  const [storageBlob, setStorageBlob] = useState(null);

  // ── Load data ──
  useEffect(() => {
    if (workstation) {
      setForm(mapWorkstationToForm(workstation));
    }
  }, [workstation]);

  /** Vuelve a leer el equipo: estado, versión y salud del agente. */
  const refrescarDetalle = useCallback(async () => {
    if (!workstation?.id || !companyId) return null;
    try {
      const res = await api.getWorkstation(workstation.id, companyId);
      const fresh = res?.data;
      if (!fresh) return null;
      setAgentStatus(fresh.agent_status || 'unknown');
      setAgentLastSeen(fresh.last_seen_at || null);
      setAgentRuntime(fresh.agent_runtime || null);
      setAgentVersion(fresh.agent_version || null);
      setInstallChannel(fresh.install_channel || null);
      setInstallVersion(fresh.install_version || null);
      setSalud(pickSalud(fresh));
      return fresh;
    } catch (_) {
      return null;
    }
  }, [workstation?.id, companyId]);

  // Tras una actualización remota el equipo vuelve con otra versión, pero la
  // ficha se quedaba con la vieja hasta pulsar F5. Mientras el comando está
  // vivo se refresca sola cada 10 s, y una vez más al confirmarse el cambio.
  const updateStatus = useAgentUpdateStatus(companyId, {
    ciType: 'workstation',
    ciId: workstation?.id,
    agentId: workstation?.agent_id,
  });
  useEffect(() => {
    if (!updateStatus) return undefined;
    if (['updating', 'unconfirmed'].includes(updateStatus.state)) {
      const timer = setInterval(refrescarDetalle, 10000);
      return () => clearInterval(timer);
    }
    if (updateStatus.state === 'updated') refrescarDetalle();
    return undefined;
  }, [updateStatus?.state, refrescarDetalle]);

  useEffect(() => {
    if (!isEditing || !workstation?.id || !companyId) return undefined;
    let cancelled = false;

    (async () => {
      const fresh = await refrescarDetalle();
      if (cancelled || !fresh) return;
      setForm(mapWorkstationToForm(fresh));
    })();

    return () => {
      cancelled = true;
    };
  }, [isEditing, workstation?.id, companyId, refrescarDetalle]);

  useEffect(() => {
    setAgentStatus(workstation?.agent_status || 'unknown');
    setAgentLastSeen(workstation?.last_seen_at || null);
    setAgentRuntime(workstation?.agent_runtime || null);
    setSalud(pickSalud(workstation));
    // Los campos de salud llegan dentro de `workstation`; se listan los que
    // cambian de verdad para no recalcular en cada render del padre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    workstation?.id,
    workstation?.agent_status,
    workstation?.last_seen_at,
    workstation?.agent_runtime,
    workstation?.bitlocker_state,
    workstation?.bitlocker_unprotected_count,
    workstation?.updates_state,
    workstation?.updates_pending_count,
    workstation?.updates_critical_count,
    workstation?.updates_reboot_required,
    workstation?.av_state,
    workstation?.inventory_updated_at,
    workstation?.risk,
  ]);

  useEffect(() => {
    if (!socket || !workstation?.id) return undefined;
    const onAgentStatus = ({ id, ci_type, agent_status, last_seen_at, agent_runtime }) => {
      if ((ci_type && ci_type !== 'workstation') || Number(id) !== Number(workstation.id)) {
        return;
      }
        setAgentStatus(agent_status);
        setAgentLastSeen(last_seen_at);
        if (agent_runtime) setAgentRuntime(agent_runtime);
    };
    socket.on('agent:status', onAgentStatus);
    return () => socket.off('agent:status', onAgentStatus);
  }, [socket, workstation?.id]);

  useEffect(() => {
    if (!companyId) return;
    api.getLocations('', companyId)
      .then(r => setLocations(r.data || []))
      .catch(() => {});
  }, [companyId]);

  const loadCatalogs = useCallback(async () => {
    if (!companyId) return;
    try {
      const [mfrRes, modelRes] = await Promise.all([
        api.getManufacturers(companyId, { deviceType: 'workstation' }).catch(() => ({ data: [] })),
        api.getDeviceModels(companyId, { deviceType: 'workstation' }).catch(() => ({ data: [] })),
      ]);
      setManufacturerOptions(Array.isArray(mfrRes?.data) ? mfrRes.data : []);
      setModelOptions(Array.isArray(modelRes?.data) ? modelRes.data : []);
    } catch (_) { /* ignore */ }
  }, [companyId]);

  useEffect(() => { loadCatalogs(); }, [loadCatalogs]);

  useEffect(() => {
    // El inventario del agente se carga SIEMPRE que haya equipo guardado, no
    // solo al editar: la pestaña Hardware y el contador de discos tienen que
    // enseñar lo que reportó el agente aunque la ficha se abra en lectura.
    if (!workstation?.id || !companyId) {
      setDiskCount(0);
      setComponentCount(0);
      setUsersCount(0);
      setStorageBlob(null);
      return;
    }

    Promise.all([
      api.getWorkstationStorage(workstation.id, companyId).catch(() => ({ data: null })),
      api.getWorkstationComponents(workstation.id, companyId).catch(() => ({ data: [] })),
      api.getWorkstationUsers(workstation.id, companyId).catch(() => ({ data: null })),
    ]).then(([storageRes, componentsRes, usersRes]) => {
      // `{ success, data: <registro> }` → `data.data` es el blob del agente.
      const storageData = storageRes?.data?.data || null;
      const disks = storageData?.physical_disks;
      const comps = componentsRes?.data;
      const usersData = usersRes?.data?.data || usersRes?.data || {};
      const preferredUser = resolveValidatedInventoryUser(usersData);
      const sessions = Array.isArray(usersData.current_sessions) ? usersData.current_sessions.length : 0;
      const localUsers = Array.isArray(usersData.local_users) ? usersData.local_users.length : 0;
      const validated = usersData.validated_user ? 1 : 0;
      setStorageBlob(storageData);
      setDiskCount(Array.isArray(disks) ? disks.length : 0);
      setComponents(Array.isArray(comps) ? comps : []);
      setComponentCount(Array.isArray(comps) ? comps.length : 0);
      setUsersCount(Math.max(sessions, localUsers, validated));
      if (isEditing && preferredUser) {
        setForm((prev) => (prev.assigned_to ? prev : { ...prev, assigned_to: preferredUser }));
      }
    });
  }, [isEditing, workstation?.id, companyId]);

  // ── Handlers ──
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setIsDirty(true);
  }, []);

  const handleDiscard = () => {
    if (workstation) {
      setForm(mapWorkstationToForm(workstation));
    } else {
      onClose();
    }
    setIsDirty(false);
    setApiError(null);
  };

  const handleSuggest = async () => {
    try {
      setSuggesting(true);
      const res = await api.suggestName({ type: 'workstation', companyId, consume: true });
      const suggested = res.data?.suggested_name || res.data?.name || '';
      if (suggested) { setForm(f => ({ ...f, name: suggested })); setIsDirty(true); }
    } catch {} finally { setSuggesting(false); }
  };

  /**
   * ✨ del campo Código: pide el siguiente número a la nomenclatura de la
   * empresa (WST-0001…) y lo deja reservado. Mismo comportamiento que el resto
   * de fichas (PeripheralForm, WifiAccessPointForm…): si el equipo aún no tiene
   * nombre, el código sugiere también el nombre, porque en un ordenador el
   * código es como se le identifica.
   */
  const handleSuggestCode = async () => {
    if (!companyId) return;
    try {
      setSuggestingCode(true);
      const res = await api.suggestName({
        type: 'workstation',
        companyId,
        locationId: form.location_id || undefined,
        consume: true,
      });
      const suggested = res.data?.suggested_name || res.data?.name || '';
      if (suggested) {
        setForm(f => ({ ...f, code: suggested, name: f.name || suggested }));
        setIsDirty(true);
      }
    } catch (err) {
      setApiError(err.message || 'No se pudo generar el código');
    } finally {
      setSuggestingCode(false);
    }
  };

  const handleSubmit = async (e, { keepEditing = false } = {}) => {
    if (e) e.preventDefault();
    if (!editing) return false;
    if (!form.code.trim() || !form.name.trim()) {
      setApiError('Código y nombre son obligatorios');
      return false;
    }
    setSaving(true);
    setApiError(null);
    try {
      const payload = {
        ...form,
        ram_gb:      form.ram_gb  ? parseInt(form.ram_gb, 10)  : null,
        disk_gb:     form.disk_gb ? parseInt(form.disk_gb, 10) : null,
        location_id: form.location_id ? parseInt(form.location_id, 10) : null,
      };
      let creado = null;
      if (isEditing) {
        await api.updateWorkstation(workstation.id, payload, companyId);
      } else {
        creado = registroGuardado(await api.createWorkstation(payload, companyId));
      }
      setIsDirty(false);
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
      // El alta se queda abierta en edición con su id: el listado adopta el
      // registro y aparecen las zonas que solo existen una vez guardado.
      onSave?.(creado);
      if (isEditing && !keepEditing) setEditing(false);
      return true;
    } catch (err) {
      setApiError(err.message || 'Error al guardar');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    setSaving(true);
    try {
      await api.deleteWorkstation(workstation.id, companyId);
      onClose();
    } catch (err) {
      setApiError(err.message || 'Error al eliminar');
    } finally {
      setSaving(false);
    }
  };

  // ── Derived ──
  const locationName = locations.find(l => String(l.id) === String(form.location_id))?.name || '';
  const deviceInfo   = DEVICE_TYPES[form.device_type] || DEVICE_TYPES.pc;
  // Con agente, MAC/IP principal/serie son de inventario y no se editan.
  const inventoryLocked = Boolean(workstation?.agent_id);

  // Discos tal cual los reportó el agente. Si hay alguno, la pestaña Hardware
  // los enseña y esconde los campos manuales: el agente manda.
  const agentDisks = Array.isArray(storageBlob?.physical_disks)
    ? storageBlob.physical_disks
    : [];
  const agentTotalGb = agentDisks.reduce(
    (total, disk) => total + (Number(disk?.capacity_gb) || 0),
    0,
  );

  // Capacidad y tipo que se enseñan en el resumen: primero el agente, luego lo
  // manual. Antes el KPI y «Cómputo y SO» solo miraban `form.disk_gb`, que está
  // vacío cuando el equipo lo inventaría el agente.
  const agentDiskTypes = [...new Set(
    agentDisks
      .map((disk) => String(disk?.disk_type || '').trim().toUpperCase())
      .filter(Boolean),
  )];
  const effectiveDiskGb = agentTotalGb > 0 ? agentTotalGb : (form.disk_gb || null);
  const effectiveDiskType = agentDiskTypes.length
    ? agentDiskTypes.join(' / ')
    : String(form.disk_type || '').toUpperCase();
  const diskLabel = effectiveDiskGb
    ? `${effectiveDiskGb} GB${effectiveDiskType ? ` ${effectiveDiskType}` : ''}`
    : '';

  // Etiqueta de la pestaña de diagnóstico del agente.
  const agentBadgeLabel = 'Agente';

  // BitLocker: lo publica el agente dentro del blob de almacenamiento
  // (`storage.data.bitlocker`). Sin dato = el equipo no lo ha reportado todavía.
  const bitlocker = storageBlob?.bitlocker || null;
  const bitlockerInfo = (() => {
    if (!bitlocker) return null;
    if (!bitlocker.available) {
      return {
        label: 'No disponible',
        klass: 'sf2-pill--wa',
        detail: bitlocker.error || 'El agente no pudo leer el estado de BitLocker.',
      };
    }
    const sinCifrar = bitlocker.unprotected_drives || [];
    if (bitlocker.protected === true) {
      return {
        label: 'Cifrado',
        klass: 'sf2-pill--ok',
        detail: sinCifrar.length ? `Volúmenes sin cifrar: ${sinCifrar.join(', ')}` : null,
      };
    }
    if (bitlocker.protected == null) {
      return {
        label: 'Desconocido',
        klass: 'sf2-pill--wa',
        detail: null,
      };
    }
    // Disco cifrado pero con la protección suspendida: no es lo mismo que «sin
    // cifrar», y decirlo mal asusta al que lee la ficha.
    const volumenes = Array.isArray(bitlocker.volumes) ? bitlocker.volumes : [];
    const cifrados = volumenes.filter(v => /fullyencrypted|^encrypted/i.test(String(v.volume_status || '')));
    if (cifrados.length > 0) {
      return {
        label: 'Cifrado, protección desactivada',
        klass: 'sf2-pill--wa',
        detail: 'El disco está cifrado, pero BitLocker no está protegiendo la clave.',
      };
    }
    return {
      label: 'Sin cifrar',
      klass: 'sf2-pill--wa',
      detail: sinCifrar.length ? `Volúmenes sin cifrar: ${sinCifrar.join(', ')}` : null,
    };
  })();

  // Windows Update: el agente publica pendientes, historial y política en el
  // mismo blob de almacenamiento (`storage.data.windows_updates`).
  const windowsUpdates = storageBlob?.windows_updates || null;
  const updatesInfo = (() => {
    if (!windowsUpdates) return null;
    if (windowsUpdates.search_ok === false) {
      return {
        label: 'No disponible',
        klass: 'sf2-pill--wa',
        detail: windowsUpdates.error || 'El agente no pudo consultar Windows Update.',
      };
    }
    const pendientes = Number(windowsUpdates.pending_count || 0);
    if (pendientes === 0) {
      return {
        label: 'Al día',
        klass: 'sf2-pill--ok',
        detail: windowsUpdates.reboot_required ? 'Reinicio pendiente para terminar la instalación' : null,
      };
    }
    const criticas = Number(windowsUpdates.critical_count || 0);
    return {
      label: `${pendientes} pendiente${pendientes === 1 ? '' : 's'}`,
      klass: criticas > 0 ? 'sf2-pill--wa' : 'sf2-pill--info',
      detail: criticas > 0 ? `${criticas} crítica(s)` : null,
    };
  })();
  // `updatesPending`/`updatesRecent` se quedaron sin uso al mover el detalle de
  // Windows Update a su propia pestaña; el KPI de General sigue con `updatesInfo`.

  // ── Variante tarjetas ──
  const mapaUbicaciones = useMemo(() => buildLocationMap(locations), [locations]);
  const rutaUbicacion = form.location_id ? locationPath(mapaUbicaciones, form.location_id) : '';
  const ESTADO_COLOR = { active: '#16a34a', maintenance: '#f59e0b', decommissioned: '#dc2626', inactive: '#94a3b8' };
  const AGENTE = {
    online:  { label: 'Online',     color: '#16a34a' },
    offline: { label: 'Offline',    color: '#dc2626' },
    unknown: { label: 'Sin agente', color: '#94a3b8' },
  };
  const agenteInfo = AGENTE[agentStatus] || AGENTE.unknown;
  // Sin agente no hay salud que enseñar: se avisa en vez de pintar ceros.
  const tieneAgente = Boolean(workstation?.agent_id) || agentStatus !== 'unknown';
  // Equipo inventariado por la red (WinRM): la ficha existe aunque no haya
  // agente instalado. Se dice de dónde salen los datos y cuándo se vieron.
  const descubrimiento = workstation?.configuration || {};
  const origenRed = (descubrimiento.discovery_sources || []).includes('winrm');
  const descubiertoEn = descubrimiento.discovery?.last_seen_at
    ? new Date(descubrimiento.discovery.last_seen_at).toLocaleString('es-ES')
    : '';
  const ultimaSenal = agentLastSeen ? new Date(agentLastSeen).toLocaleString('es-ES') : '—';
  // La versión del instalador sirve de respaldo si el agente aún no reportó la
  // suya (equipos recién instalados, primer arranque).
  const versionAgente = agentVersion || installVersion || null;
  // Mismo criterio que la lista: si va por detrás de lo publicado, se avisa.
  const agenteDesfasado = Boolean(
    versionAgente && latestAgentVersion && versionIsOlder(versionAgente, latestAgentVersion),
  );
  /** Cancelar edición: recarga lo guardado y sale de edición **sin cerrar** la ficha. */
  const handleCancelEdit = () => { handleDiscard(); setEditing(false); };

  const filteredModelOptions = useMemo(() => {
    const matchedMfr = manufacturerOptions.find(m => (m.name || '').toLowerCase() === (form.manufacturer || '').toLowerCase());
    const mid = matchedMfr?.id;
    return (modelOptions || []).filter(m => !mid || m.manufacturer_id === mid || !m.manufacturer_id);
  }, [modelOptions, manufacturerOptions, form.manufacturer]);

  const saveManufacturerToCatalog = async (nameOverride) => {
    const name = nameOverride || form.manufacturer;
    if (!name || !companyId) return;
    try {
      await api.createManufacturer({ name, device_type: 'workstation' }, companyId);
      await loadCatalogs();
    } catch (e) { /* ignore duplicate */ }
  };

  const saveModelToCatalog = async (modelNameOverride) => {
    const modelName = modelNameOverride || form.model;
    if (!modelName || !companyId) return;
    const matchedMfr = manufacturerOptions.find(m => (m.name || '').toLowerCase() === (form.manufacturer || '').toLowerCase());
    try {
      await api.createDeviceModel({ name: modelName, device_type: 'workstation', manufacturer_id: matchedMfr?.id }, companyId);
      await loadCatalogs();
    } catch (e) { /* ignore duplicate */ }
  };

  // Active Directory solo tiene sentido en un Windows **unido a dominio**: en
  // un equipo con WORKGROUP la pestaña no aporta nada y solo mete ruido.
  const esWindows = String(form.os_name || '').toLowerCase().includes('windows');
  const dominioDeclarado = String(form.domain_name || '').trim();
  const enDominio = Boolean(form.part_of_domain)
    || Boolean(dominioDeclarado && !/^workgroup$/i.test(dominioDeclarado))
    || Boolean(workstation?.configuration?.ad_client?.joined);
  const muestraAd = esWindows && enDominio;

  // Sin las actualizaciones de Windows: esas van en su propia pestaña.
  const softwareCount = filterSoftwareEntries(storageBlob?.installed_software).length;

  const tabs = [
    { key: 'identificacion', label: 'Identificación' },
    { key: 'ubicacion',      label: 'Ubicación' },
    { key: 'hardware',       label: 'Hardware' },
    { key: 'software',       label: `Software${softwareCount ? ` (${softwareCount})` : ''}` },
    { key: 'access',         label: `Accesos${accessCount > 0 ? ` (${accessCount})` : ''}` },
    { key: 'red',            label: 'Red' },
    ...(esWindows ? [{ key: 'updates', label: 'Actualizaciones' }] : []),
    ...(muestraAd ? [{ key: 'ad', label: 'Active Directory' }] : []),
    { key: 'agente',         label: agentBadgeLabel },
  ];

  // En tarjetas no hay pestañas «Identificación» ni «Ubicación»: esa información
  // se edita en el Resumen.
  const tabsDef = isCards
    ? [
        { key: 'resumen',  label: 'General' },
        { key: 'hardware', label: 'Hardware' },
        { key: 'rendimiento', label: 'Rendimiento' },
        { key: 'software', label: tabs[3].label },
        { key: 'red',      label: 'Red' },
        ...(isEditing ? [{ key: 'access', label: tabs[4].label }] : []),
        ...(esWindows ? [{ key: 'updates', label: 'Actualizaciones' }] : []),
        ...(muestraAd ? [{ key: 'ad', label: 'Active Directory' }] : []),
        // Los planes son tareas, no un dato del equipo: tienen su pestaña.
        { key: 'planes',   label: 'Planes' },
        { key: 'agente',   label: agentBadgeLabel },
      ]
    : tabs;

  // Si el equipo deja de estar en dominio (o los datos llegan después), la
  // pestaña AD desaparece: no se puede quedar seleccionada una pestaña invisible.
  useEffect(() => {
    if (activeTab === 'ad' && !muestraAd) {
      setActiveTab(isCards ? 'resumen' : 'identificacion');
    }
    if (activeTab === 'updates' && !esWindows) {
      setActiveTab(isCards ? 'resumen' : 'identificacion');
    }
  }, [activeTab, muestraAd, esWindows, isCards]);

  return (
    <div className="dv-overlay">
      {/* ── TopBar ── */}
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={() => navGuard.request(() => onClose())}>
          <ArrowLeftIcon /> Ordenadores
        </button>
        <DetailCrumb label={form.name} />
        <FormPager
          items={navItems}
          index={navIndex}
          label="Ordenador"
          disabled={saving}
          onChange={(destino) => navGuard.request(() => { setIsDirty(false); onNavigate?.(destino); })}
        />
        {navGuard.dialog}
        <div className="dv-topbar-spacer" />

        {savedOk && (
          <span style={{ color: '#16a34a', fontSize: '0.78rem', fontWeight: 600 }}>✓ Guardado</span>
        )}
        {apiError && (
          <span style={{ color: '#dc2626', fontSize: '0.78rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            ⚠ {apiError}
          </span>
        )}

        <div className="dv-topbar-actions">
          {isCards ? (
            <>
              {!editing && isEditing && (
                <button className="dv-btn dv-btn-secondary" type="button" onClick={() => setEditing(true)} disabled={saving} title="Editar la ficha">
                  <PencilSquareIcon /> Editar
                </button>
              )}
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
                <XMarkIcon /> Cerrar
              </button>
              {isEditing && (
                <button className="dv-btn dv-btn-danger" type="button" onClick={() => setShowDeleteConfirm(true)} disabled={saving}>
                  <TrashIcon />
                </button>
              )}
            </>
          ) : (
          <>
          <span className={`dv-status-badge ${form.status === 'active' ? 'dv-status-active' : 'dv-status-inactive'}`}>
            ● {STATUS_OPTS.find(s => s.value === form.status)?.label || form.status}
          </span>

          {/* En LECTURA solo se ofrece pasar a edición; en EDICIÓN, descartar y guardar. */}
          {!editing && isEditing && (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={() => setEditing(true)} disabled={saving}>
              <PencilSquareIcon /> Editar
            </button>
          )}

          {editing && isEditing && (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={handleDiscard} disabled={saving}>
              <XMarkIcon /> Descartar
            </button>
          )}

          <button
            className="dv-btn dv-btn-primary"
            type="button"
            onClick={handleSubmit}
            disabled={!editing || saving || (isEditing && !isDirty)}
          >
            {saving ? <span className="dv-spinner" /> : <CheckIcon />}
            {saving ? 'Guardando…' : 'Guardar'}
          </button>

          {isEditing && (
            <button className="dv-btn dv-btn-danger" type="button" onClick={() => setShowDeleteConfirm(true)} disabled={saving}>
              <TrashIcon />
            </button>
          )}
          </>
          )}
        </div>
      </div>

      {/* ── SmartBar (solo en la ficha clásica) ── */}
      {!isCards && (
      <div className="dv-smartbar">
        <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('identificacion')}>
          <span className="dv-smart-btn-icon" style={{ fontSize: 16 }}>{deviceInfo.icon}</span>
          <span className="dv-smart-btn-value">{deviceInfo.label}</span>
        </button>

        {form.cpu && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('hardware')}>
            <span className="dv-smart-btn-icon"><CpuChipIcon style={{ width: 18, height: 18, color: '#7c3aed' }} /></span>
            <span className="dv-smart-btn-value" style={{ color: '#7c3aed', fontSize: '0.78rem', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{form.cpu}</span>
          </button>
        )}

        {form.ram_gb && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('hardware')}>
            <span className="dv-smart-btn-value" style={{ color: '#2563eb' }}>{form.ram_gb} GB</span>
            <span className="dv-smart-btn-label">RAM</span>
          </button>
        )}

        {diskLabel && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('hardware')}>
            <span className="dv-smart-btn-value" style={{ color: '#059669', fontSize: '0.8rem' }}>{diskLabel}</span>
          </button>
        )}

        {form.os_name && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('red')}>
            <span className="dv-smart-btn-icon"><ComputerDesktopIcon style={{ width: 18, height: 18, color: '#0369a1' }} /></span>
            <span className="dv-smart-btn-value" style={{ color: '#0369a1', fontSize: '0.82rem' }}>{form.os_name}{form.os_version ? ` ${form.os_version}` : ''}</span>
          </button>
        )}

        {(form.domain_name != null || form.part_of_domain != null) && (
          <button className="dv-smart-btn" type="button">
            <span className="dv-smart-btn-icon"><BuildingOfficeIcon style={{ width: 18, height: 18, color: form.part_of_domain ? '#0891b2' : '#6b7280' }} /></span>
            <span className="dv-smart-btn-value" style={{ color: form.part_of_domain ? '#0891b2' : '#6b7280', fontSize: '0.82rem', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {form.part_of_domain ? (form.domain_name || 'Dominio') : (form.domain_name || 'Grupo de trabajo')}
            </span>
            <span className="dv-smart-btn-label">{form.part_of_domain ? 'Dominio' : 'Workgroup'}</span>
          </button>
        )}

        {form.ip_address && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('red')}>
            <span className="dv-smart-btn-icon"><WifiIcon style={{ width: 18, height: 18, color: '#d97706' }} /></span>
            <span className="dv-smart-btn-value" style={{ color: '#d97706', fontSize: '0.8rem' }}>{form.ip_address}</span>
          </button>
        )}

        {form.assigned_to && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('ubicacion')}>
            <span className="dv-smart-btn-icon"><UserIcon style={{ width: 18, height: 18, color: '#9333ea' }} /></span>
            <span className="dv-smart-btn-value" style={{ color: '#9333ea', fontSize: '0.82rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{form.assigned_to}</span>
          </button>
        )}

        {locationName && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('ubicacion')}>
            <span className="dv-smart-btn-icon"><MapPinIcon style={{ width: 18, height: 18, color: '#dc2626' }} /></span>
            <span className="dv-smart-btn-value" style={{ color: '#dc2626', fontSize: '0.82rem' }}>{locationName}</span>
          </button>
        )}

        {isEditing && (
          <button className="dv-smart-btn" type="button" onClick={() => setShowDisksPanel(true)}>
            <span className="dv-smart-btn-value" style={{ color: '#059669' }}>{diskCount}</span>
            <span className="dv-smart-btn-label">Discos</span>
          </button>
        )}

        {isEditing && (
          <button className="dv-smart-btn" type="button" onClick={() => setShowComponentsPanel(true)}>
            <span className="dv-smart-btn-value" style={{ color: '#2563eb' }}>{componentCount}</span>
            <span className="dv-smart-btn-label">Componentes</span>
          </button>
        )}

        {isEditing && (
          <button className="dv-smart-btn" type="button" onClick={() => setShowUsersPanel(true)}>
            <span className="dv-smart-btn-icon"><UserIcon style={{ width: 18, height: 18, color: '#9333ea' }} /></span>
            <span className="dv-smart-btn-value" style={{ color: '#9333ea' }}>{usersCount}</span>
            <span className="dv-smart-btn-label">Usuarios</span>
          </button>
        )}

        {isEditing && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('identificacion')}>
            <span
              className="dv-smart-btn-icon"
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: agentStatus === 'online' ? '#22c55e' : agentStatus === 'offline' ? '#ef4444' : '#6b7280',
                boxShadow: agentStatus === 'online' ? '0 0 6px rgba(34,197,94,.6)' : 'none',
              }}
            />
            <span className="dv-smart-btn-value" style={{ color: agentStatus === 'online' ? '#22c55e' : agentStatus === 'offline' ? '#ef4444' : '#6b7280' }}>
              {agentStatus === 'online' ? 'Online' : agentStatus === 'offline' ? 'Offline' : 'Sin agente'}
            </span>
            <span className="dv-smart-btn-label">Agente</span>
          </button>
        )}

        {isEditing && (
          <DocSmartButton elementType="workstation" elementId={workstation.id} elementName={form.name} companyId={companyId} />
        )}
      </div>
      )}

      {/* ── Body ── */}
      <div className="dv-body">
        {/* Main panel */}
        <fieldset className={`dv-fieldset${isCards ? ' sf2-main' : ''}`} disabled={isCards ? false : !editing}>
        <form className={`dv-main${isCards ? ' sf2-main' : ''}`} onSubmit={handleSubmit}>

          <FormCardsShell
            isCards={isCards}
            header={isCards ? (
              <FormCardsHead
                icon={<ComputerDesktopIcon style={{ width: 20, height: 20 }} />}
                title={form.name}
                onTitleChange={(v) => { setForm(f => ({ ...f, name: v })); setIsDirty(true); }}
                placeholder="Nombre del dispositivo…"
                editing={editing}
                saving={saving}
                onSave={handleSubmit}
                onCancel={handleCancelEdit}
                onEdit={() => setEditing(true)}
                badges={[
                  { text: STATUS_OPTS.find(s => s.value === form.status)?.label || form.status, color: ESTADO_COLOR[form.status] || '#94a3b8' },
                  { text: `Agente: ${agenteInfo.label}`, color: agenteInfo.color },
                  ...(form.os_name ? [{ text: `${form.os_name}${form.os_version ? ` ${form.os_version}` : ''}` }] : []),
                ]}
                chips={[
                  // CPU, RAM, disco e IP ya están en la tarjeta de identificación
                  // del General: repetirlos aquí solo robaba una fila de cabecera.
                  { value: diskCount, label: 'discos', onClick: isEditing ? () => setShowDisksPanel(true) : undefined, flat: !isEditing },
                  { value: componentCount, label: 'componentes', onClick: isEditing ? () => setShowComponentsPanel(true) : undefined, flat: !isEditing },
                  { value: usersCount, label: 'usuarios', onClick: isEditing ? () => setShowUsersPanel(true) : undefined, flat: !isEditing },
                  ...(accessCount > 0 ? [{ value: accessCount, label: 'accesos', onClick: () => setActiveTab('access') }] : []),
                ]}
                extra={(
                  <>
                    {workstation?.id && (
                      <DocSmartButton elementType="workstation" elementId={workstation.id} elementName={form.name} companyId={companyId} variant="chip" />
                    )}
                    {workstation?.id && (
                      <ContactsSmartButton
                        companyId={companyId}
                        objectType="workstation"
                        objectId={workstation.id}
                        objectName={form.name}
                      />
                    )}
                    <HelpDot topic="listados.workstation_inventory" label="Ayuda: la ficha del ordenador" />
                  </>
                )}
              />
            ) : (
              <>
                {/* Record title */}
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <input
                    className="dv-record-title"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Nombre del dispositivo…"
                    required
                    style={{ flex: 1 }}
                  />
                  <button type="button" title="Sugerir nombre" onClick={handleSuggest} disabled={suggesting}
                    style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: 8, cursor: suggesting ? 'wait' : 'pointer', color: suggesting ? '#94a3b8' : '#7c3aed', flexShrink: 0 }}>
                    <SparklesIcon style={{ width: 18, height: 18 }} />
                  </button>
                </div>
              </>
            )}
            tabs={(
              <div className="dv-tabs-nav">
            {tabsDef.map(tab => (
              <TabButton
                key={tab.key}
                type="button"
                className={`dv-tab-btn${activeTab === tab.key ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </TabButton>
            ))}
              </div>
            )}
          >
          {/* Las acciones remotas viven en la pestaña Agente: son operaciones
              sobre el equipo y en General estorbaban a quien solo venía a ver
              la ficha. La consola funciona con la ficha en modo lectura. */}
          {/* Un `fieldset<fieldset disabled>` desactiva **todos** sus controles,
              botones incluidos. Las pestañas «Agente», «Active Directory»,
              «Actualizaciones» y «Software» son de solo lectura y gestionan sus
              propios permisos: se abre el fieldset para que funcionen sin entrar
              en edición (antes el buscador de software salía deshabilitado). */}
          <fieldset disabled={!editing && activeTab !== 'agente' && activeTab !== 'ad' && activeTab !== 'updates' && activeTab !== 'software'} className="dv-fieldset">

          {/* ── Tab: Resumen (solo tarjetas) ── */}
          {isCards && activeTab === 'resumen' && (
            <div className="dv-tab-pane">
              <div className="sf2-hl" style={{ marginBottom: 12 }}>
                <div className="sf2-kpi"><span>RAM</span><b>{form.ram_gb ? `${form.ram_gb} GB` : '—'}</b></div>
                <div className="sf2-kpi"><span>Disco</span><b style={{ fontSize: '0.95rem' }}>{diskLabel || '—'}</b></div>
                <div className="sf2-kpi"><span>Componentes</span><b>{componentCount}</b></div>
                <div className="sf2-kpi"><span>Usuarios</span><b>{usersCount}</b></div>
                <div className="sf2-kpi"><span>Agente</span><b style={{ fontSize: '0.95rem' }}>{agenteInfo.label}</b></div>
                <div className="sf2-kpi"><span>Cifrado</span><b style={{ fontSize: '0.95rem' }}>{bitlockerInfo ? bitlockerInfo.label : '—'}</b></div>
                <div className="sf2-kpi"><span>Actualizaciones</span><b style={{ fontSize: '0.95rem' }}>{updatesInfo ? updatesInfo.label : '—'}</b></div>
              </div>

              {/* Rendimiento en vivo del equipo: CPU, RAM, disco más lleno y
                  servicios. Lo alimenta el agente en cada ciclo. */}
              <ServerLiveMonitor
                ciType="workstation"
                serverId={workstation?.id}
                companyId={companyId}
                hasAgent={tieneAgente}
              />

              {/* Salud del equipo: mismos datos y umbrales que el dashboard. */}
              <div className="sf2-card" style={{ marginBottom: 12 }}>
                <h3>Salud del equipo</h3>
                {!tieneAgente ? (
                  <div className="sf2-hint" style={{ margin: 0 }}>
                    {origenRed ? (
                      <>
                        Este equipo se ha inventariado <strong>sin agente</strong>, por la red
                        (WinRM){descubiertoEn ? ` · último barrido ${descubiertoEn}` : ''}.
                        Hay hardware, SO, antivirus y software, pero no cifrado, parches ni
                        eventos: eso llega al instalar el agente.
                      </>
                    ) : (
                      <>
                        Este equipo no tiene agente instalado, así que no hay datos de cifrado,
                        parches ni antivirus. En cuanto el agente reporte, aquí verás su salud
                        igual que en el dashboard de estaciones de trabajo.
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="sf2-fields" style={{ '--n': 4 }}>
                      <div className="sf2-field">
                        <label>Cifrado (BitLocker)</label>
                        <div className="v">
                          <span className={`sf2-pill sf2-pill--${SALUD_BITLOCKER[salud.bitlocker]?.pill || 'info'}`}>
                            {SALUD_BITLOCKER[salud.bitlocker]?.label || 'Sin datos'}
                          </span>
                        </div>
                        <span className="sf2-hint" style={{ marginTop: 2 }}>
                          {salud.unprotected > 0
                            ? `${salud.unprotected} volumen(es) sin cifrar`
                            : 'Sin volúmenes sin cifrar'}
                        </span>
                      </div>
                      <div className="sf2-field">
                        <label>Windows Update</label>
                        <div className="v">
                          <span className={`sf2-pill sf2-pill--${SALUD_UPDATES[salud.updates]?.pill || 'info'}`}>
                            {SALUD_UPDATES[salud.updates]?.label || 'Sin datos'}
                          </span>
                        </div>
                        <span className="sf2-hint" style={{ marginTop: 2 }}>
                          {[salud.pending ? `${salud.pending} pendiente(s)` : null,
                            salud.critical ? `${salud.critical} crítica(s)` : null,
                            salud.reboot ? 'reinicio pendiente' : null]
                            .filter(Boolean).join(' · ') || 'Nada pendiente'}
                        </span>
                      </div>
                      <div className="sf2-field">
                        <label>Antivirus</label>
                        <div className="v">
                          <span className={`sf2-pill sf2-pill--${SALUD_AV[salud.av]?.pill || 'info'}`}>
                            {SALUD_AV[salud.av]?.label || 'Sin datos'}
                          </span>
                        </div>
                        <span className="sf2-hint" style={{ marginTop: 2 }}>
                          {salud.avProduct || (salud.avProducts.length
                            ? `${salud.avProducts.length} producto(s)`
                            : 'Sin producto detectado')}
                        </span>
                      </div>
                      <div className="sf2-field">
                        <label>Riesgo</label>
                        <div className="v">
                          <span className={`sf2-pill sf2-pill--${SALUD_RIESGO[salud.risk?.level]?.pill || 'ok'}`}>
                            {SALUD_RIESGO[salud.risk?.level]?.label || 'Sin riesgo'}
                          </span>
                        </div>
                        <span className="sf2-hint" style={{ marginTop: 2 }}>
                          {salud.risk?.reasons?.length
                            ? `${salud.risk.reasons.length} motivo(s)`
                            : 'Ninguna regla disparada'}
                        </span>
                      </div>
                    </div>

                    {Array.isArray(salud.risk?.reasons) && salud.risk.reasons.length > 0 && (
                      <div className="sf2-field" style={{ marginTop: 10 }}>
                        <label>Motivos</label>
                        <div className="v" style={{ flexWrap: 'wrap', gap: 6 }}>
                          {salud.risk.reasons.map(reason => (
                            <span
                              key={reason.code}
                              className={`sf2-pill sf2-pill--${PILL_SEVERIDAD[reason.severity] || 'info'}`}
                              title={`Gravedad: ${reason.severity}`}
                            >
                              {reason.label}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="sf2-hint" style={{ marginTop: 10 }}>
                      Agente {agenteInfo.label} · última señal {ultimaSenal}
                      {salud.inventoryAt && ` · inventario ${new Date(salud.inventoryAt).toLocaleString('es-ES')}`}
                      {salud.osBuild && ` · build ${salud.osBuild}`}
                    </div>
                  </>
                )}
              </div>

              <div className="sf2-grid sf2-grid--2">
                <div className="sf2-card">
                  <h3>Identificación{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field">
                      <label>Código *</label>
                      {editing ? (
                        <div className="sf2-input-row">
                          <input value={form.code} onChange={e => handleChange({ target: { name: 'code', value: e.target.value } })} placeholder="WST-0001" />
                          <button
                            type="button"
                            className="sf2-icon-btn"
                            title="Generar código con la nomenclatura"
                            aria-label="Generar código con la nomenclatura"
                            onClick={handleSuggestCode}
                            disabled={suggestingCode}
                          >
                            {suggestingCode ? <span className="dv-spinner" /> : <SparklesIcon style={{ width: 16, height: 16 }} />}
                          </button>
                        </div>
                      ) : <div className="v">{form.code || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Tipo</label>
                      {editing ? (
                        <select value={form.device_type} onChange={e => handleChange({ target: { name: 'device_type', value: e.target.value } })}>
                          <option value="pc">🖥️ PC Escritorio</option>
                          <option value="laptop">💻 Portátil</option>
                        </select>
                      ) : <div className="v">{deviceInfo.label}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Estado</label>
                      {editing ? (
                        <select value={form.status} onChange={e => handleChange({ target: { name: 'status', value: e.target.value } })}>
                          {STATUS_OPTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      ) : <div className="v">{STATUS_OPTS.find(s => s.value === form.status)?.label || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Fabricante</label>
                      {editing
                        ? <input value={form.manufacturer} onChange={e => handleChange({ target: { name: 'manufacturer', value: e.target.value } })} placeholder="Dell, HP…" />
                        : <div className="v">{form.manufacturer || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Modelo</label>
                      {editing
                        ? <input value={form.model} onChange={e => handleChange({ target: { name: 'model', value: e.target.value } })} />
                        : <div className="v">{form.model || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Nº de serie</label>
                      {editing
                        ? <input
                            value={form.serial_number}
                            onChange={e => handleChange({ target: { name: 'serial_number', value: e.target.value } })}
                            readOnly={inventoryLocked}
                            style={inventoryLocked ? LOCK_STYLE : undefined}
                            title={inventoryLocked ? 'Lo gestiona el agente CMDB' : undefined}
                          />
                        : <div className="v">{form.serial_number || '—'}</div>}
                      {inventoryLocked && <div className="sf2-hint">🔒 Inventario del agente</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Asset tag</label>
                      {editing
                        ? <input value={form.asset_tag} onChange={e => handleChange({ target: { name: 'asset_tag', value: e.target.value } })} />
                        : <div className="v">{form.asset_tag || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Fecha de compra</label>
                      {editing
                        ? <input type="date" value={form.purchase_date} onChange={e => handleChange({ target: { name: 'purchase_date', value: e.target.value } })} />
                        : <div className="v">{form.purchase_date || '—'}</div>}
                    </div>
                  </div>
                </div>

                <div className="sf2-card">
                  <h3>Cómputo y SO{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field">
                      <label>CPU</label>
                      {editing
                        ? <input value={form.cpu} onChange={e => handleChange({ target: { name: 'cpu', value: e.target.value } })} placeholder="i5-1235U" />
                        : <div className="v">{form.cpu || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>RAM (GB)</label>
                      {editing
                        ? <input type="number" min="0" value={form.ram_gb} onChange={e => handleChange({ target: { name: 'ram_gb', value: e.target.value } })} />
                        : <div className="v">{form.ram_gb ? `${form.ram_gb} GB` : '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Tipo de disco</label>
                      {editing ? (
                        <select value={form.disk_type} onChange={e => handleChange({ target: { name: 'disk_type', value: e.target.value } })}>
                          <option value="ssd">SSD</option>
                          <option value="hdd">HDD</option>
                          <option value="nvme">NVMe</option>
                        </select>
                      ) : <div className="v">{effectiveDiskType || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Disco (GB)</label>
                      {editing
                        ? <input type="number" min="0" value={form.disk_gb} onChange={e => handleChange({ target: { name: 'disk_gb', value: e.target.value } })} />
                        : <div className="v">{effectiveDiskGb ? `${effectiveDiskGb} GB` : '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>SO</label>
                      {editing
                        ? <input value={form.os_name} onChange={e => handleChange({ target: { name: 'os_name', value: e.target.value } })} placeholder="Windows" />
                        : <div className="v">{form.os_name || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Versión del SO</label>
                      {editing
                        ? <input value={form.os_version} onChange={e => handleChange({ target: { name: 'os_version', value: e.target.value } })} placeholder="11 Pro" />
                        : <div className="v">{form.os_version || '—'}</div>}
                    </div>
                    <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Notas</label>
                      {editing ? (
                        <textarea className="dv-input" rows={3} value={form.notes} onChange={e => handleChange({ target: { name: 'notes', value: e.target.value } })} placeholder="Observaciones…" />
                      ) : <div className="v">{form.notes || '—'}</div>}
                    </div>
                  </div>
                </div>

                <div className="sf2-card">
                  <h3>Sistema y antigüedad{inventoryLocked && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>inventario</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field">
                      <label>BIOS (fecha)</label>
                      <div className="v">
                        {fmtFecha(workstation?.bios_release_date)}
                        {workstation?.bios_release_date && (
                          <span className="sf2-hint" style={{ marginLeft: 6 }}>
                            · {antiguedadDesde(workstation.bios_release_date)} de antigüedad
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="sf2-field">
                      <label>Versión de BIOS</label>
                      <div className="v">{workstation?.bios_version || '—'}</div>
                    </div>
                    <div className="sf2-field">
                      <label>Windows instalado el</label>
                      <div className="v">
                        {fmtFecha(workstation?.os_install_date)}
                        {workstation?.os_install_date && (
                          <span className="sf2-hint" style={{ marginLeft: 6 }}>
                            · {antiguedadDesde(workstation.os_install_date)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="sf2-field">
                      <label>Licencia de Windows</label>
                      <div className="v" style={{ flexWrap: 'wrap', gap: 6 }}>
                        <span>
                          {[workstation?.license_channel, workstation?.license_partial_key && `•••••-${workstation.license_partial_key}`]
                            .filter(Boolean).join(' · ') || '—'}
                        </span>
                        {workstation?.license_status && (
                          <span className={`sf2-pill sf2-pill--${workstation.license_status === 'Activada' ? 'ok' : 'wa'}`}>
                            {workstation.license_status}
                          </span>
                        )}
                        {workstation?.license_oem_in_firmware && (
                          <span className="sf2-pill sf2-pill--info" title="Clave OEM grabada en el firmware (SLIC/MSDM)">OEM en firmware</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {!inventoryLocked && (
                    <div className="sf2-hint" style={{ marginTop: 10 }}>
                      Estos datos llegan del agente. Instálalo en el equipo para rellenarlos solos.
                    </div>
                  )}
                </div>

                <div className="sf2-card">
                  <h3>Usuario y ubicación{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Asignado a</label>
                      {editing
                        ? <input value={form.assigned_to} onChange={e => handleChange({ target: { name: 'assigned_to', value: e.target.value } })} placeholder="Nombre del usuario" />
                        : <div className="v">{form.assigned_to || '—'}</div>}
                    </div>
                    <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Ubicación</label>
                      {editing ? (
                        <LocationTreeSelect
                          locations={locations}
                          value={form.location_id}
                          onChange={(id) => handleChange({ target: { name: 'location_id', value: id } })}
                          placeholder="— Sin ubicación —"
                        />
                      ) : <div className="v">{rutaUbicacion || locationName || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Planta</label>
                      {editing
                        ? <input value={form.floor} onChange={e => handleChange({ target: { name: 'floor', value: e.target.value } })} placeholder="1ª, Baja…" />
                        : <div className="v">{form.floor || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Sala</label>
                      {editing
                        ? <input value={form.room} onChange={e => handleChange({ target: { name: 'room', value: e.target.value } })} placeholder="Sala principal" />
                        : <div className="v">{form.room || '—'}</div>}
                    </div>
                  </div>
                </div>

                <div className="sf2-card">
                  <h3>Red y agente</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field">
                      <label>MAC</label>
                      {editing
                        ? <input
                            value={form.mac_address}
                            onChange={e => handleChange({ target: { name: 'mac_address', value: e.target.value } })}
                            placeholder="00:11:22:33:44:55"
                            readOnly={inventoryLocked}
                            style={inventoryLocked ? LOCK_STYLE : undefined}
                            title={inventoryLocked ? 'Lo gestiona el agente CMDB' : undefined}
                          />
                        : <div className="v">{form.mac_address || '—'}</div>}
                      {inventoryLocked && <div className="sf2-hint">🔒 Inventario del agente</div>}
                    </div>
                    <div className="sf2-field">
                      <label>IP</label>
                      {editing
                        ? <input
                            value={form.ip_address}
                            onChange={e => handleChange({ target: { name: 'ip_address', value: e.target.value } })}
                            placeholder="192.168.1.100"
                            readOnly={inventoryLocked}
                            style={inventoryLocked ? LOCK_STYLE : undefined}
                            title={inventoryLocked ? 'Lo gestiona el agente CMDB' : undefined}
                          />
                        : <div className="v">{form.ip_address || '—'}</div>}
                      {inventoryLocked && <div className="sf2-hint">🔒 IP principal del agente · las IPs adicionales se añaden en IPAM</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Dominio / grupo</label>
                      {editing
                        ? <input value={form.domain_name || ''} onChange={e => handleChange({ target: { name: 'domain_name', value: e.target.value } })} placeholder="corp.empresa.local" />
                        : <div className="v">{form.domain_name || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>En dominio</label>
                      <label className="sf2-switch sf2-switch--sm">
                        <input
                          type="checkbox"
                          checked={!!form.part_of_domain}
                          onChange={e => handleChange({ target: { name: 'part_of_domain', type: 'checkbox', checked: e.target.checked } })}
                          disabled={!editing || form.part_of_domain == null}
                        />
                        <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                        <span className="sf2-switch-label">{form.part_of_domain ? 'Sí' : 'No'}</span>
                      </label>
                    </div>
                    <div className="sf2-field"><label>Última señal</label><div className="v">{ultimaSenal}</div></div>
                    <div className="sf2-field">
                      <label>Runtime del agente</label>
                      {/* `agent_runtime` es un objeto (mode, transport, connected, diagnostics…):
                          pintarlo tal cual rompe React (#31, objetos no válidos como hijo). */}
                      <div className="v">
                        {agentRuntime
                          ? [
                              agentRuntime.mode || 'polling',
                              agentRuntime.transport || null,
                              agentRuntime.connected ? null : 'desconectado',
                            ].filter(Boolean).join(' · ')
                          : '—'}
                      </div>
                    </div>
                    <div className="sf2-field">
                      <label>Versión del agente</label>
                      <div className="v">
                        {versionAgente ? `v${versionAgente}` : '—'}
                        {agentVersion && installVersion && installVersion !== agentVersion && (
                          <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                            {' '}(instalador v{installVersion})
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="sf2-field">
                      <label>Instalación</label>
                      <div className="v">{CHANNEL_LABELS[installChannel] || '—'}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                    {isEditing && (
                      <span className="sf2-link" role="button" tabIndex={0}
                        onClick={() => setShowDisksPanel(true)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setShowDisksPanel(true); } }}
                      >Ver discos ({diskCount}) →</span>
                    )}
                    {isEditing && (
                      <span className="sf2-link" role="button" tabIndex={0}
                        onClick={() => setShowComponentsPanel(true)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setShowComponentsPanel(true); } }}
                      >Ver componentes ({componentCount}) →</span>
                    )}
                    {isEditing && (
                      <span className="sf2-link" role="button" tabIndex={0}
                        onClick={() => setShowUsersPanel(true)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setShowUsersPanel(true); } }}
                      >Ver usuarios ({usersCount}) →</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Identificación ── */}
          {activeTab === 'identificacion' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <h3 className="dv-section-title">Datos del dispositivo</h3>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Código <span style={{ color: '#dc2626' }}>*</span></label>
                    <input className="form-control" name="code" value={form.code} onChange={handleChange} placeholder="PC-001" required />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Tipo</label>
                    <select className="form-select" name="device_type" value={form.device_type} onChange={handleChange}>
                      <option value="pc">🖥️ PC Escritorio</option>
                      <option value="laptop">💻 Portátil</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Estado</label>
                    <select className="form-select" name="status" value={form.status} onChange={handleChange}>
                      {STATUS_OPTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <h3 className="dv-section-title">Inventario</h3>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Asset Tag</label>
                    <input className="form-control" name="asset_tag" value={form.asset_tag} onChange={handleChange} placeholder="ASSET-001" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Número de serie</label>
                    <input className="form-control" name="serial_number" value={form.serial_number} onChange={handleChange} placeholder="SN-XXXXXXXX" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Fecha de compra</label>
                    <input className="form-control" type="date" name="purchase_date" value={form.purchase_date} onChange={handleChange} />
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <h3 className="dv-section-title">Sistema operativo</h3>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">SO</label>
                    <select className="form-select" name="os_name" value={form.os_name} onChange={handleChange}>
                      <option value="Windows">Windows</option>
                      <option value="Linux">Linux</option>
                      <option value="macOS">macOS</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Versión</label>
                    <input className="form-control" name="os_version" value={form.os_version} onChange={handleChange} placeholder="11, 22.04, 14.2…" />
                  </div>
                </div>
              </div>

              {isEditing && (
                <div className="dv-section">
                  <h3 className="dv-section-title">Agente</h3>
                  {(() => {
                    const mode = String(agentRuntime?.mode || '').trim().toLowerCase();
                    const label = mode === 'websocket'
                      ? 'WebSocket realtime'
                      : mode === 'polling'
                        ? 'Polling fallback (WebSocket no disponible)'
                        : null;
                    const detail = mode === 'polling' ? agentRuntime?.last_error || null : null;
                    if (!label) return null;
                    return (
                      <div style={{
                        marginBottom: 10, display: 'inline-flex', alignItems: 'center', gap: 6,
                        background: mode === 'websocket' ? 'rgba(34,197,94,.1)' : 'rgba(245,158,11,.1)',
                        border: `1px solid ${mode === 'websocket' ? 'rgba(34,197,94,.3)' : 'rgba(245,158,11,.3)'}`,
                        borderRadius: 6, padding: '3px 10px', fontSize: '0.78rem',
                        color: mode === 'websocket' ? '#16a34a' : '#d97706',
                      }}>
                        {mode === 'websocket' ? '⚡' : '🔄'} {label}
                        {detail ? ` — ${detail}` : ''}
                      </div>
                    );
                  })()}
                  <AgentStatusBadge
                    status={agentStatus}
                    lastSeenAt={agentLastSeen}
                    agentId={workstation?.agent_id}
                    companyId={companyId}
                    ciType="workstation"
                    ciId={workstation?.id}
                    showRescan={agentStatus !== 'unknown'}
                  />
                  {workstation?.agent_version && (
                    <div style={{ marginTop: 8, color: '#64748b', fontSize: '0.82rem' }}>
                      Versión del agente: {workstation.agent_version}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Tab: Hardware ── */}
          {activeTab === 'hardware' && (
            <div className="dv-tab-pane">
              {/* Lo que el agente ve dentro del equipo, sin abrir el modal. */}
              <WorkstationHardwareList components={components} />

              <div className="dv-section">
                <h3 className="dv-section-title">Almacenamiento</h3>

                {/* Si el agente ha reportado discos, manda el agente: la tabla
                    los enseña tal cual y los campos manuales no se tocan. Solo
                    cuando no hay agente se edita a mano. */}
                {agentDisks.length > 0 ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                      <span className="sf2-pill sf2-pill--ok">{agentDisks.length} disco{agentDisks.length === 1 ? '' : 's'}</span>
                      <span className="sf2-pill">{agentTotalGb} GB en total</span>
                      <span className="sf2-hint" style={{ margin: 0 }}>
                        Detectado por el agente. Para corregir un disco, usa «Ver discos».
                      </span>
                      <button
                        type="button"
                        className="dv-btn dv-btn-secondary"
                        onClick={() => setShowDisksPanel(true)}
                        style={{ marginLeft: 'auto' }}
                      >
                        Ver discos
                      </button>
                    </div>
                    <div style={{ display: 'grid', gap: 6 }}>
                      {agentDisks.map((disk) => (
                        <div
                          key={disk.id || disk.slot}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            flexWrap: 'wrap',
                            padding: '0.5rem 0.7rem',
                            border: '1px solid var(--border)',
                            borderRadius: 8,
                            background: 'var(--bg-card)',
                            fontSize: '0.82rem',
                          }}
                        >
                          <b style={{ minWidth: 74 }}>{disk.slot || '—'}</b>
                          <span style={{ flex: '1 1 200px', minWidth: 160 }}>
                            {[disk.manufacturer, disk.model].filter(Boolean).join(' ') || 'Disco sin modelo'}
                          </span>
                          <span className="sf2-pill">{disk.capacity_gb ? `${disk.capacity_gb} GB` : 'Capacidad sin dato'}</span>
                          {disk.disk_type && <span className="sf2-pill sf2-pill--ac">{disk.disk_type}</span>}
                          {disk.interface && <span className="sf2-pill">{disk.interface}</span>}
                          {disk.smart_status && (
                            <span className={`sf2-pill ${/ok|good|healthy/i.test(disk.smart_status) ? 'sf2-pill--ok' : 'sf2-pill--wa'}`}>
                              {disk.smart_status}
                            </span>
                          )}
                          {disk.serial && (
                            <span className="sf2-hint" style={{ margin: 0, marginLeft: 'auto' }}>
                              S/N {disk.serial}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">Tipo de disco</label>
                      <select className="form-select" name="disk_type" value={form.disk_type} onChange={handleChange}>
                        <option value="ssd">SSD</option>
                        <option value="hdd">HDD</option>
                        <option value="mixed">Mixto</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Capacidad (GB)</label>
                      <input className="form-control" type="number" min="1" name="disk_gb" value={form.disk_gb} onChange={handleChange} placeholder="512" />
                    </div>
                  </div>
                )}
                {agentDisks.length === 0 && (
                  <p className="sf2-hint" style={{ marginTop: 8 }}>
                    Sin discos del agente: estos dos campos son manuales. En cuanto el agente
                    reporte, la tabla los sustituye.
                  </p>
                )}

                {/* Cifrado: tarjeta con el mismo lenguaje que el resto de la
                    ficha (pill de estado + filas por volumen), no un bloque
                    suelto con estilos en línea. */}
                <div className="sf2-card sf2-bitlocker">
                  <h3>
                    Cifrado de disco (BitLocker)
                    {bitlockerInfo && (
                      <span className={`sf2-pill ${bitlockerInfo.klass}`}>{bitlockerInfo.label}</span>
                    )}
                  </h3>

                  {!bitlockerInfo && (
                    <div className="sf2-hint" style={{ margin: 0 }}>
                      Sin datos del agente todavía. En cuanto reporte, aquí saldrá el estado de cada volumen.
                    </div>
                  )}

                  {bitlockerInfo && (
                    <div className="sf2-fields" style={{ '--n': 2 }}>
                      <div className="sf2-field">
                        <label>Estado</label>
                        <div className="v">
                          <span className={`sf2-pill ${bitlockerInfo.klass}`}>{bitlockerInfo.label}</span>
                        </div>
                      </div>
                      <div className="sf2-field">
                        <label>Disco del sistema</label>
                        <div className="v">{bitlocker?.system_drive || '—'}</div>
                        {bitlockerInfo.detail && <div className="sf2-hint">{bitlockerInfo.detail}</div>}
                      </div>
                    </div>
                  )}

                  {Array.isArray(bitlocker?.volumes) && bitlocker.volumes.length > 0 && (
                    <div className="sf2-bl-vols">
                      {bitlocker.volumes.map(vol => (
                        <div key={vol.mount_point} className="sf2-bl-vol">
                          <b className="sf2-bl-vol-mount">{vol.mount_point}</b>
                          <span className="sf2-bl-vol-status">{vol.volume_status || '—'}</span>
                          <span className="sf2-bl-vol-meta">
                            {[vol.encryption_method,
                              typeof vol.encryption_percentage === 'number' ? `${vol.encryption_percentage}%` : null,
                              vol.lock_status && vol.lock_status !== 'Unlocked' ? vol.lock_status : null]
                              .filter(Boolean).join(' · ') || '—'}
                          </span>
                          <span
                            className={`sf2-pill ${vol.protected === true ? 'sf2-pill--ok' : vol.protected === false ? 'sf2-pill--wa' : ''}`}
                            title={`Protección: ${vol.protection_status || 'sin dato'}`}
                          >
                            {vol.protection_status === 'On' || vol.protected === true
                              ? 'Protegido'
                              : vol.protection_status === 'Off' || vol.protected === false
                                ? 'Protección desactivada'
                                : (vol.protection_status || 'Sin datos')}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {bitlocker && bitlocker.source && (
                    <div className="sf2-hint" style={{ marginTop: 8 }}>
                      Fuente: {bitlocker.source}
                      {bitlocker.collected_at ? ` · ${new Date(bitlocker.collected_at).toLocaleString('es-ES')}` : ''}
                    </div>
                  )}
                </div>
              </div>

            </div>
          )}

          {/* ── Tab: Rendimiento ── */}
          {activeTab === 'rendimiento' && (
            <div className="dv-tab-pane">
              <WorkstationPerformancePanel
                companyId={companyId}
                workstationId={workstation?.id}
                hasAgent={tieneAgente}
              />
            </div>
          )}

          {/* ── Tab: Planes ── */}
          {activeTab === 'planes' && (
            <div className="dv-tab-pane">
              <WorkstationPlansCard
                companyId={companyId}
                ciType="workstation"
                ciId={workstation?.id}
              />
            </div>
          )}

          {/* ── Tab: Software ── */}
          {activeTab === 'software' && (
            <div className="dv-tab-pane">
              <SoftwareInventoryPanel
                companyId={companyId}
                hostCiType="workstation"
                hostCiId={workstation?.id}
                fallbackEntries={Array.isArray(storageBlob?.installed_software) ? storageBlob.installed_software : []}
              />
              {workstation?.id && (
                <div style={{ marginTop: 16 }}>
                  <SoftwarePackagesPanel
                    companyId={companyId}
                    ciType="workstation"
                    ciId={workstation.id}
                    hostName={workstation.name}
                    compact
                  />
                </div>
              )}
            </div>
          )}

          {/* ── Tab: Red ── */}
          {activeTab === 'red' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <h3 className="dv-section-title">Conectividad</h3>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">MAC Address</label>
                    <input
                      className="form-control"
                      name="mac_address"
                      value={form.mac_address}
                      onChange={handleChange}
                      placeholder="00:11:22:33:44:55"
                      readOnly={inventoryLocked}
                      style={inventoryLocked ? LOCK_STYLE : undefined}
                    />
                    {inventoryLocked && <div className="sf2-hint">🔒 Inventario del agente</div>}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">IP Address <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>(IP principal – se sincroniza con IPAM)</span></label>
                    <input
                      className="form-control"
                      name="ip_address"
                      value={form.ip_address}
                      onChange={handleChange}
                      placeholder="192.168.1.100"
                      readOnly={inventoryLocked}
                      style={inventoryLocked ? LOCK_STYLE : undefined}
                    />
                    {inventoryLocked && <div className="sf2-hint">🔒 IP principal del agente · añade otras IPs en la tarjeta IPAM</div>}
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <h3 className="dv-section-title">IPAM — Asignaciones de IP</h3>
                <ElementIpPanel
                  objectType="workstation"
                  objectId={workstation?.id}
                  companyId={companyId}
                  elementName={workstation?.name || form.name}
                />
              </div>
            </div>
          )}
          {/* ── Tab: Actualizaciones de Windows ── */}
          {activeTab === 'updates' && esWindows && (
            <WorkstationUpdatesPanel
              updates={storageBlob?.windows_updates}
              companyId={companyId}
              agentId={workstation?.agent_id}
              ciType="workstation"
              ciId={workstation?.id}
            />
          )}
          {/* ── Tab: Active Directory (Windows unido a dominio) ── */}
          {activeTab === 'ad' && muestraAd && (
            <WorkstationAdPanel companyId={companyId} workstation={workstation} />
          )}
          {/* ── Tab: Agente (log y ejecuciones) ── */}
          {activeTab === 'agente' && (
            <div className="dv-tab-pane">
              {/* Acciones remotas primero: es lo que se usa al abrir la pestaña.
                  El formulario va plegado y el historial solo enseña lo que está
                  en curso (o lo reciente). */}
              {workstation?.id && (
                <RemoteActions
                  companyId={companyId}
                  ciType="workstation"
                  ciId={workstation.id}
                />
              )}
              <div style={{ marginTop: 16 }}>
                <AgentActivityPanel
                  companyId={companyId}
                  ciType="workstation"
                  ciId={workstation?.id}
                  agentId={workstation?.agent_id}
                  agentVersion={workstation?.agent_version}
                  agentStatus={workstation?.agent_status || 'unknown'}
                  lastSeenAt={workstation?.last_seen_at}
                  latestVersion={latestAgentVersion}
                />
              </div>
            </div>
          )}
          {/* ── Tab: Accesos ── */}
          {activeTab === 'access' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <DeviceAccessPanel
                  objectType="workstation"
                  objectId={workstation?.id}
                  companyId={companyId}
                  elementName={workstation?.name || form.name}
                  defaultHost={form.ip_address}
                  onCountChange={setAccessCount}
                  canEdit={editing}
                />
              </div>
            </div>
          )}

          {/* ── Tab: Ubicación ── */}
          {activeTab === 'ubicacion' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <h3 className="dv-section-title">Asignación</h3>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Asignado a</label>
                    <input className="form-control" name="assigned_to" value={form.assigned_to} onChange={handleChange} placeholder="Nombre del usuario" />
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <h3 className="dv-section-title">Ubicación física</h3>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Sala</label>
                    <input className="form-control" name="room" value={form.room} onChange={handleChange} placeholder="Sala principal" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Planta</label>
                    <input className="form-control" name="floor" value={form.floor} onChange={handleChange} placeholder="1ª, Baja…" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Ubicación</label>
                  <LocationTreeSelect
                    locations={locations}
                    value={form.location_id}
                    onChange={(id) => handleChange({ target: { name: 'location_id', value: id } })}
                    placeholder="— Sin ubicación —"
                  />
                  </div>
                </div>
              </div>
            </div>
          )}
          </fieldset>
          <FormDirtyDock
            show={isCards && isDirty}
            saving={saving}
            onDiscard={handleDiscard}
            onSave={() => handleSubmit(null)}
          />
          </FormCardsShell>
        </form>
        </fieldset>

      {/* ── Sidebar: chatter / notes ── */}
        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType="workstation"
          ciId={workstation?.id}
          tipoLegible="el equipo"
        />
      </div>

      {showDisksPanel && isEditing && (
        <ServerDisksPanel
          server={workstation}
          companyId={companyId}
          onClose={() => setShowDisksPanel(false)}
          getStorage={(workstationId, currentCompanyId) => api.getWorkstationStorage(workstationId, currentCompanyId)}
          updateStorage={async (workstationId, data, currentCompanyId) => {
            const result = await api.updateWorkstationStorage(workstationId, data, currentCompanyId);
            const nextStorage = result?.data?.data || null;
            const disks = nextStorage?.physical_disks;
            setStorageBlob(nextStorage);
            setDiskCount(Array.isArray(disks) ? disks.length : 0);
            onSave?.();
            return result;
          }}
        />
      )}

      {showComponentsPanel && isEditing && (
        <ServerComponentsPanel
          server={workstation}
          companyId={companyId}
          onClose={() => setShowComponentsPanel(false)}
          onUpdated={async () => {
            try {
              const [storageRes, componentsRes] = await Promise.all([
                api.getWorkstationStorage(workstation.id, companyId).catch(() => ({ data: null })),
                api.getWorkstationComponents(workstation.id, companyId).catch(() => ({ data: [] })),
              ]);
              const nextStorage = storageRes?.data?.data || null;
              const disks = nextStorage?.physical_disks;
              const comps = componentsRes?.data;
              setStorageBlob(nextStorage);
              setDiskCount(Array.isArray(disks) ? disks.length : 0);
              setComponentCount(Array.isArray(comps) ? comps.length : 0);
            } finally {
              onSave?.();
            }
          }}
          getStorage={(workstationId, currentCompanyId) => api.getWorkstationStorage(workstationId, currentCompanyId)}
          updateStorage={(workstationId, data, currentCompanyId) => api.updateWorkstationStorage(workstationId, data, currentCompanyId)}
          getComponents={(workstationId, currentCompanyId) => api.getWorkstationComponents(workstationId, currentCompanyId)}
          createComponent={(workstationId, data, currentCompanyId) => api.createWorkstationComponent(workstationId, data, currentCompanyId)}
          updateComponent={(workstationId, componentId, data, currentCompanyId) => api.updateWorkstationComponent(workstationId, componentId, data, currentCompanyId)}
          deleteComponent={(workstationId, componentId, currentCompanyId) => api.deleteWorkstationComponent(workstationId, componentId, currentCompanyId)}
          createComponentPort={(workstationId, componentId, data, currentCompanyId) => api.createWorkstationComponentPort(workstationId, componentId, data, currentCompanyId)}
          updateComponentPort={(workstationId, componentId, portId, data, currentCompanyId) => api.updateWorkstationComponentPort(workstationId, componentId, portId, data, currentCompanyId)}
          deleteComponentPort={(workstationId, componentId, portId, currentCompanyId) => api.deleteWorkstationComponentPort(workstationId, componentId, portId, currentCompanyId)}
          entityLabel="Ordenador"
          allowCopy={false}
        />
      )}

      {showUsersPanel && isEditing && (
        <SystemUsersPanel
          record={workstation}
          companyId={companyId}
          getUsers={(workstationId, currentCompanyId) => api.getWorkstationUsers(workstationId, currentCompanyId)}
          entityLabel="Ordenador"
          onClose={async () => {
            setShowUsersPanel(false);
            try {
              const usersRes = await api.getWorkstationUsers(workstation.id, companyId).catch(() => ({ data: null }));
              const usersData = usersRes?.data?.data || usersRes?.data || {};
              const preferredUser = resolveValidatedInventoryUser(usersData);
              const sessions = Array.isArray(usersData.current_sessions) ? usersData.current_sessions.length : 0;
              const localUsers = Array.isArray(usersData.local_users) ? usersData.local_users.length : 0;
              const validated = usersData.validated_user ? 1 : 0;
              setUsersCount(Math.max(sessions, localUsers, validated));
              if (preferredUser) {
                setForm((prev) => (prev.assigned_to ? prev : { ...prev, assigned_to: preferredUser }));
              }
            } finally {
              onSave?.();
            }
          }}
        />
      )}

      {/* Diálogo: eliminar dispositivo */}
      {showDeleteConfirm && (
        <Dialog
          title="Eliminar dispositivo"
          onClose={() => setShowDeleteConfirm(false)}
          busy={saving}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => setShowDeleteConfirm(false)} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="list-btn-danger" onClick={handleDelete} disabled={saving}>
                {saving ? 'Eliminando…' : 'Eliminar'}
              </button>
            </>
          )}
        >
          <p>
            ¿Seguro que quieres eliminar <strong>{form.name || workstation?.name}</strong>?{' '}
            Se eliminarán también sus componentes y conexiones. No se puede deshacer.
          </p>
        </Dialog>
      )}
    </div>
  );
}

export default WorkstationForm;
